// Toast notifications — iter31 rewrite, iter42 enhancements.
//
// - NO auto-dismiss. Toasts stay until the user clicks ✕.
// - ✕ close button is always visible (not hover-only) — only closes, never
//   navigates (separate from body click).
// - Max 5 visible at a time (newest first / on top); older toasts are
//   discarded visually but remain in /notifications log.
// - S1 / breach toasts get a red pulsing border for war-room visibility.
// - Iter42: title 15px / body 13px / 14px padding; if `toast.link` is provided
//   (`{type: "incident"|"sorting"|"8d"|"fmea", id: "..."}`) the body becomes
//   clickable to navigate to the entity + auto-dismiss.
import React from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../store";

const MAX_VISIBLE = 5;

const TYPE_STYLE = {
  s1: {
    border: "#E24B4A",
    pulse: true,
    icon: (
      <span
        className="inline-block rounded-full pulse-red"
        style={{ width: 9, height: 9, background: "#E24B4A" }}
      />
    ),
  },
  warning: {
    border: "#f3b14a",
    pulse: false,
    icon: (
      <span
        className="inline-block rounded-full"
        style={{ width: 9, height: 9, background: "#f3b14a", boxShadow: "0 0 8px rgba(243,177,74,0.6)" }}
      />
    ),
  },
  info: {
    border: "#185FA5",
    pulse: false,
    icon: (
      <span
        className="inline-block rounded-full"
        style={{ width: 9, height: 9, background: "#5fa3e3", boxShadow: "0 0 8px rgba(95,163,227,0.6)" }}
      />
    ),
  },
  success: {
    border: "#4ed084",
    pulse: false,
    icon: (
      <span
        className="inline-block rounded-full"
        style={{ width: 9, height: 9, background: "#4ed084", boxShadow: "0 0 8px rgba(78,208,132,0.6)" }}
      />
    ),
  },
  error: {
    border: "#E24B4A",
    pulse: false,
    icon: (
      <span
        className="inline-block rounded-full"
        style={{ width: 9, height: 9, background: "#E24B4A" }}
      />
    ),
  },
};

// Map a `link` payload to the destination URL the body click should navigate to.
const linkToHref = (link) => {
  if (!link || !link.id) return null;
  switch (link.type) {
    case "incident":  return `/incidents?focus=${encodeURIComponent(link.id)}`;
    case "sorting":   return `/sorting?focus=${encodeURIComponent(link.id)}`;
    case "tms":       return `/tms?focus=${encodeURIComponent(link.id)}`;
    case "8d":        return `/8d?focus=${encodeURIComponent(link.id)}`;
    case "fmea":      return `/fmea?focus=${encodeURIComponent(link.id)}`;
    case "service_request": return `/incidents?focus=${encodeURIComponent(link.incident_id || link.id)}`;
    default:          return null;
  }
};

const ToastItem = ({ toast }) => {
  const removeToast = useStore((s) => s.removeToast);
  const openPanel = useStore((s) => s.openPanel);
  const navigate = useNavigate();
  const [hover, setHover] = React.useState(false);
  const cfg = TYPE_STYLE[toast.type] || TYPE_STYLE.info;
  const handleClose = (e) => {
    e.stopPropagation();
    removeToast(toast.id);
  };

  const href = linkToHref(toast.link);
  const clickable = Boolean(href);
  const onBodyClick = () => {
    if (!clickable) return;
    // For incident links, open the side panel via store as well as the route.
    if (toast.link?.type === "incident" && openPanel) {
      openPanel(toast.link.id);
    }
    navigate(href);
    removeToast(toast.id);
  };

  return (
    <div
      className={"toast-enter " + (cfg.pulse ? "toast-pulse-red " : "")}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 380,
        background: clickable && hover ? "#1a1f30" : "#111420",
        borderRadius: 6,
        border: `1px solid ${cfg.pulse ? cfg.border : "rgba(255,255,255,0.08)"}`,
        borderLeft: `3px solid ${cfg.border}`,
        padding: "14px 38px 14px 14px",
        display: "flex",
        gap: 11,
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        cursor: clickable ? "pointer" : "default",
        transition: "background-color 120ms ease",
      }}
      data-testid={`toast-${toast.type}`}
      data-toast-id={toast.id}
      data-toast-clickable={clickable ? "true" : "false"}
      onClick={onBodyClick}
      onMouseEnter={() => clickable && setHover(true)}
      onMouseLeave={() => clickable && setHover(false)}
    >
      <div className="flex items-start pt-1">{cfg.icon}</div>
      <div className="flex-1 min-w-0">
        <div
          style={{
            fontFamily: "'Syne', system-ui, sans-serif",
            fontWeight: 600,
            fontSize: 15,
            color: "#f0f2f8",
            letterSpacing: "0.2px",
            lineHeight: 1.25,
          }}
          data-testid="toast-title"
        >
          {toast.title}
        </div>
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 13,
            color: "#c5cad8",
            marginTop: 4,
            lineHeight: 1.4,
            wordBreak: "break-word",
          }}
          data-testid="toast-body"
        >
          {toast.body}
        </div>
        {toast.action && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toast.action.onClick();
              removeToast(toast.id);
            }}
            className="mt-2"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#9ec3ec",
              letterSpacing: "0.5px",
              textDecoration: "underline",
              textUnderlineOffset: 2,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            data-testid="toast-action-btn"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Always-visible close button — never navigates */}
      <button
        onClick={handleClose}
        aria-label="Close notification"
        data-testid="toast-close-btn"
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 24,
          height: 24,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 4,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.10)",
          color: "#9198b0",
          cursor: "pointer",
        }}
      >
        <X size={13} />
      </button>
    </div>
  );
};

export const ToastViewport = () => {
  const toasts = useStore((s) => s.toasts);
  // Newest on top → reverse, then cap at MAX_VISIBLE.
  const visible = [...toasts].reverse().slice(0, MAX_VISIBLE);

  return (
    <div
      className="fixed z-[200] flex flex-col items-end gap-[7px]"
      style={{
        // Iter79 — raise above the Copilot FAB (56px circle anchored at
        // bottom: 24px right: 24px). 24 (fab bottom) + 56 (fab height) + 12 (gap) = 92px.
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 92px)",
        right: 16,
        left: "auto",
        maxWidth: "calc(100vw - 32px)",
        pointerEvents: "none",
      }}
      data-testid="toast-viewport"
      data-toast-count={toasts.length}
      data-toast-visible={visible.length}
    >
      {visible.map((t) => (
        <div
          key={t.id}
          className="w-full sm:w-auto"
          style={{ pointerEvents: "auto", width: "min(380px, calc(100vw - 32px))" }}
        >
          <ToastItem toast={t} />
        </div>
      ))}
    </div>
  );
};
